import React from 'react';

import {
  Alert,
  AlertTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { green, orange, red } from '@mui/material/colors';

import type { AvailableTravelDays } from './availableTravelDates';
import type { AllocationState, MaterialisationRow } from './computeMaterialisation';
import {
  MATERIALISATION_PCT_HEALTHY,
  MATERIALISATION_PCT_LOW,
  MATERIALISATION_PCT_OVER,
} from './computeMaterialisation';

interface Props {
  rows: Array<MaterialisationRow>;
  travelDays: AvailableTravelDays | null;
}

const HEAD_CELL_SX = { fontWeight: 600 };

const FIGURE_SX = { fontFamily: 'monospace', whiteSpace: 'nowrap' };

const HINT_SX = { color: 'text.disabled' };

const materialisationColor = (pct: number) => {
  if (pct > MATERIALISATION_PCT_OVER) {
    return red[700];
  }

  if (pct > MATERIALISATION_PCT_HEALTHY) {
    return green[700];
  }

  return pct > MATERIALISATION_PCT_LOW ? orange[800] : 'text.disabled';
};

const allocationLabel = (allocation: AllocationState) => {
  switch (allocation.kind) {
    case 'set':
      return allocation.rooms.toLocaleString('en-AU');
    case 'freesale':
      return 'Free sale';
    case 'unparseable':
      return allocation.text;
    default:
      return 'not set';
  }
};

const number = (value: number) => value.toLocaleString('en-AU');

export default function AllocationsTable({ rows, travelDays }: Props) {
  const aboveHealthy = rows.filter(
    (row) => row.materialisationPct !== null && row.materialisationPct > MATERIALISATION_PCT_HEALTHY,
  );

  return (
    <>
      {aboveHealthy.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <AlertTitle>
            {aboveHealthy.length} room {aboveHealthy.length === 1 ? 'type is' : 'types are'} forecast above{' '}
            {MATERIALISATION_PCT_HEALTHY}% materialisation
          </AlertTitle>
          <Stack sx={{ mt: 1 }}>
            {aboveHealthy.map((row) => (
              <Typography key={row.roomId} variant="body2">
                {row.assetName}: {row.materialisationPct?.toFixed(1)}% of allocated room nights
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}

      {travelDays && (
        <Typography color="text.secondary" sx={{ mb: 1.5 }} variant="body2">
          Based on <strong>{number(travelDays.availableDays)}</strong> available travel days
          {travelDays.blackedOutDays > 0 && (
            <>
              , excluding <strong>{number(travelDays.blackedOutDays)}</strong> blacked-out days
            </>
          )}
          . Allocated room nights = allocation per day x available travel days.
        </Typography>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={HEAD_CELL_SX}>Room type</TableCell>
              <TableCell align="right" sx={HEAD_CELL_SX}>
                Allocation per day
              </TableCell>
              <TableCell align="right" sx={HEAD_CELL_SX}>
                Forecasted packages
              </TableCell>
              <TableCell align="right" sx={HEAD_CELL_SX}>
                Forecasted room nights
              </TableCell>
              <TableCell align="right" sx={HEAD_CELL_SX}>
                Allocated room nights
              </TableCell>
              <TableCell align="right" sx={HEAD_CELL_SX}>
                % Materialisation
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.roomId}>
                <TableCell>{row.assetName}</TableCell>
                <TableCell align="right" sx={{ ...FIGURE_SX, ...(row.allocation.kind === 'set' ? {} : HINT_SX) }}>
                  {allocationLabel(row.allocation)}
                </TableCell>
                <TableCell align="right" sx={FIGURE_SX}>
                  {number(row.forecastedPackages)}
                </TableCell>
                <TableCell align="right" sx={FIGURE_SX}>
                  {number(row.forecastedRoomNights)}
                </TableCell>
                <TableCell align="right" sx={{ ...FIGURE_SX, ...(row.allocatedRoomNights === null ? HINT_SX : {}) }}>
                  {row.allocatedRoomNights === null ? '-' : number(row.allocatedRoomNights)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    ...FIGURE_SX,
                    ...(row.materialisationPct === null
                      ? HINT_SX
                      : { color: materialisationColor(row.materialisationPct), fontWeight: 500 }),
                  }}
                >
                  {row.materialisationPct === null ? '-' : `${row.materialisationPct.toFixed(1)}%`}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
